export type HelpArticle = {
  id: string;
  title: string;
  summary: string;
  body: string[];
  relatedLinks?: { label: string; href: string }[];
};

export type HelpTopic = {
  id: string;
  title: string;
  description: string;
  icon:
    | "settings"
    | "chat"
    | "phone"
    | "training"
    | "billing"
    | "integrations"
    | "handoff"
    | "agents";
  articles: HelpArticle[];
};

const PRICING_LINKS = [
  { label: "Pricing calculator", href: "/pricing" },
  { label: "Try It Free", href: "/agents/hire/custom?new=1" },
];

const DOCS_LINKS = [
  { label: "Documentation", href: "/documentation" },
  { label: "API Reference", href: "/api-reference" },
];

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: "account-setup",
    title: "Account & Setup",
    description:
      "Create your account, hire or clone an agent, and go live without locking into a flat-fee bundle.",
    icon: "settings",
    articles: [
      {
        id: "create-account",
        title: "Create an Inbound GPT account",
        summary: "Sign up and start with free Live Chat — no credit card required.",
        body: [
          "Click Sign up in the header, or start from Try It Free / Build your agent. You can configure an agent before adding paid channels.",
          "After sign-up you land in the dashboard where Profile, Responsibilities, Training, and Tools match the Product mega menu.",
          "There is no long-term contract. Enable only the channels and tools you need; cancel or change them anytime.",
        ],
        relatedLinks: [
          { label: "Try It Free", href: "/agents/hire/custom?new=1" },
          { label: "Getting started docs", href: "/documentation#getting-started" },
        ],
      },
      {
        id: "clone-or-build",
        title: "Clone a template or build custom",
        summary: "Use Agent Templates for battle-tested roles, or request a custom build.",
        body: [
          "Browse Agent Templates to clone Official or Moonlight agents by industry and role (Online Patient Coordinator, Scheduling Specialist, Front Desk Receptionist, and more).",
          "Prefer a blank slate? Use Build your agent / hire onboarding to describe responsibilities, add your website, and configure channels.",
          "Need a role that is not listed? Use Custom Agent Development or Request a Role from the Product / Agent Templates menus.",
        ],
        relatedLinks: [
          { label: "Agent Templates", href: "/agents" },
          { label: "Custom Agent Development", href: "/custom-agent-development" },
        ],
      },
      {
        id: "go-live-checklist",
        title: "Go-live checklist",
        summary: "Confirm knowledge, booking, and human handoff before you announce.",
        body: [
          "Train the agent on company profile and top FAQs, then run knowledge tests.",
          "If you book appointments, test end-to-end in chat (calendar or EMR) before enabling phone booking.",
          "Set Lead Team Member phone numbers and verify Connect with Human / enhanced transfer works with whisper context.",
          "Install the website widget and confirm greetings and CTAs on mobile and desktop.",
        ],
        relatedLinks: [
          { label: "Documentation — Go live", href: "/documentation#go-live" },
          { label: "Customer Stories", href: "/customer-stories" },
        ],
      },
    ],
  },
  {
    id: "chat-widget",
    title: "Chat Widget",
    description:
      "Install the website widget, customize branding, and keep Connect with Human available.",
    icon: "chat",
    articles: [
      {
        id: "install-widget",
        title: "Install the chat widget",
        summary: "Copy the embed snippet and place it on your site.",
        body: [
          "In project Settings → Channels → Live Chat, copy the embed code for your project.",
          "Paste the script before </body> on every page where visitors should see the agent, or load it site-wide via your tag manager.",
          "Live Chat is free with unlimited conversations — the best place to start before enabling paid channels.",
        ],
        relatedLinks: [
          { label: "Docs — Widget installation", href: "/documentation#widget" },
          { label: "Product — Responsibilities", href: "/product/responsibilities" },
        ],
      },
      {
        id: "customize-widget",
        title: "Customize greeting and CTAs",
        summary: "Match brand colors, Ask button label, and human handoff CTAs.",
        body: [
          "Update widget colors, starting message, and agent name/avatar in Appearance / Profile settings.",
          "Keep Book a Demo and Connect with Human (or your custom CTAs) so visitors can reach a person when needed — that is the human-in-the-loop model.",
          "Preview on mobile and desktop before publishing.",
        ],
        relatedLinks: [{ label: "Product — Profile", href: "/product/profile" }],
      },
      {
        id: "widget-troubleshooting",
        title: "Widget not showing or wrong agent",
        summary: "Common embed and deleted-project issues.",
        body: [
          "Confirm the script id matches your current project. If you deleted a project, remove the old embed — orphaned widgets can error for visitors.",
          "Hard-refresh or clear cache after changing the snippet. Ad blockers can hide the widget in rare cases.",
          "On marketing pages, the right-hand sidebar uses the site concierge unless you open Live Demo for a specific marketplace agent.",
        ],
        relatedLinks: DOCS_LINKS,
      },
    ],
  },
  {
    id: "phone-system",
    title: "Phone System",
    description:
      "Enable phone calls, business hours, transfers, and after-hours coverage with a human in the loop.",
    icon: "phone",
    articles: [
      {
        id: "enable-phone",
        title: "Enable Phone Calls",
        summary: "Phone is $30/mo with included free minutes, then per-minute usage.",
        body: [
          "Turn on Phone Calls under Responsibilities / Channels. Phone is $30/month with the first 20 minutes free, then $0.50/min.",
          "Assign or sync a phone number, confirm E.164 formats for test calls, and set business hours so open/closed status is accurate.",
          "Test with a known number before routing your main line.",
        ],
        relatedLinks: [
          ...PRICING_LINKS,
          { label: "Docs — Phone, SMS & email", href: "/documentation#channels" },
        ],
      },
      {
        id: "transfers-handoff",
        title: "Warm transfers and team handoff",
        summary: "Always keep a path to a human with whisper context.",
        body: [
          "Configure Lead Team Member phone numbers per location. Enhanced transfer uses Live Call Control so staff hear a short whisper summary before connecting.",
          "During business hours, emergencies and “speak to a person” requests should transfer when a transfer contact is available — not only take a message.",
          "After handoff, Conversation Summary emails help your team follow up with full context.",
        ],
        relatedLinks: [
          { label: "Docs — Team handoffs", href: "/documentation#handoffs" },
          { label: "Product — Profile (team)", href: "/product/profile" },
        ],
      },
      {
        id: "after-hours",
        title: "After-hours coverage",
        summary: "Never miss a call when the office is closed.",
        body: [
          "Keep the agent enabled after hours to answer FAQs, book appointments, or take structured messages.",
          "When transfer contacts are unavailable, the agent should collect clear callback details and notify your team.",
          "Pair phone with SMS or email follow-up if you want asynchronous confirmation.",
        ],
        relatedLinks: [{ label: "FAQ", href: "/faq" }],
      },
    ],
  },
  {
    id: "knowledge-base",
    title: "Training & Knowledge",
    description:
      "Train agents on your company profile, FAQs, and RAG sources so answers stay on-brand.",
    icon: "training",
    articles: [
      {
        id: "basic-training",
        title: "Basic training (included free)",
        summary: "Company profile and ICP power accurate, on-brand answers.",
        body: [
          "Fill company profile: services, policies, brand voice, and ideal customer profile.",
          "Basic training is included free. Advanced sources (website crawl, files, Q&A, video, audio) are available as a $35/mo add-on when enabled.",
          "Prefer short, factual Q&A for scheduling, pricing, and hours questions.",
        ],
        relatedLinks: [
          { label: "Product — Training", href: "/product/training" },
          { label: "Docs — Training", href: "/documentation#training" },
        ],
      },
      {
        id: "test-knowledge",
        title: "Test before you go live",
        summary: "Run knowledge tests and fix gaps early.",
        body: [
          "Use Test / knowledge preview to ask the same questions customers ask.",
          "Fix gaps before enabling phone or announcing the widget publicly.",
          "Review the inbox weekly for new questions that should become FAQs.",
        ],
        relatedLinks: [{ label: "Docs — Best practices", href: "/documentation#best-practices" }],
      },
    ],
  },
  {
    id: "billing-plans",
    title: "Billing & Pricing",
    description:
      "Unit-based pricing for channels and tools — not a flat $500–$1,500 AI receptionist bundle.",
    icon: "billing",
    articles: [
      {
        id: "how-pricing-works",
        title: "How unit-based pricing works",
        summary: "Pay for what you enable, with included free usage tiers.",
        body: [
          "Live Chat is free with unlimited conversations. Browser Calls, Messenger, and WhatsApp are also free when enabled.",
          "Text Messages are $10/mo (first 100 SMS free, then $0.10/SMS). Email is $15/mo unlimited. Phone is $30/mo (first 20 minutes free, then $0.50/min).",
          "Tools: EMR integrations $50/mo; Google Calendar / Calendly / Zoom $20/mo each. Advanced Agent Training $35/mo. First location and team member included free.",
          "There is no long-term contract and no credit card required to start with free Live Chat.",
        ],
        relatedLinks: PRICING_LINKS,
      },
      {
        id: "estimate-monthly-cost",
        title: "Estimate your monthly cost",
        summary: "Use the pricing calculator before you hire.",
        body: [
          "Open the Pricing page and toggle the channels, locations, team members, and tools you plan to use.",
          "A common starter stack — Live Chat + Phone + EMR — starts around $80/month before phone overage minutes.",
          "You can change enabled channels later; you are not locked into a bundled flat fee.",
        ],
        relatedLinks: PRICING_LINKS,
      },
      {
        id: "invoices-cancel",
        title: "Invoices and cancellation",
        summary: "Transparent line items; cancel anytime.",
        body: [
          "Invoices reflect the channels and tools enabled for the billing period, plus any usage beyond included free tiers.",
          "Cancel or disable paid channels anytime from project settings. Contact sales for multi-location or partner billing questions.",
        ],
        relatedLinks: [
          { label: "Contact sales", href: "/about" },
          { label: "FAQ", href: "/faq" },
        ],
      },
    ],
  },
  {
    id: "integrations",
    title: "Integrations & Tools",
    description:
      "Connect calendars, EMR/PMS, Zoom, analytics, and custom MCP workflows.",
    icon: "integrations",
    articles: [
      {
        id: "calendars-emr",
        title: "Calendars and EMR / PMS",
        summary: "Book into real systems — test in chat first.",
        body: [
          "Scheduling add-ons include Google Calendar, Calendly, and Zoom ($20/mo each).",
          "EMR / PMS integrations (Juvonno, NexHealth, Dentrix, Open Dental, and more) are $50/mo and use MCP tools so chat and phone share the same booking path.",
          "Always test booking in a sandbox or test calendar before production phone booking.",
        ],
        relatedLinks: [
          { label: "Product — Integrations", href: "/product/integrations" },
          { label: "Product — Tools", href: "/product/tools" },
          { label: "Docs — Tools", href: "/documentation#tools" },
        ],
      },
      {
        id: "automation-analytics",
        title: "Zapier, analytics, and agent contact",
        summary: "Extend workflows beyond the inbox.",
        body: [
          "Use Zapier for CRM updates and Slack alerts. WhatConverts and Google Analytics help with attribution.",
          "Optional dedicated agent phone and email keep customer communications on-brand.",
        ],
        relatedLinks: [
          { label: "Integrations", href: "/product/integrations" },
          { label: "API Reference", href: "/api-reference" },
        ],
      },
      {
        id: "custom-mcp",
        title: "Custom MCP or new role",
        summary: "Request Custom Agent Development when you need something new.",
        body: [
          "If you need a workflow or role that is not in the marketplace yet, submit Custom Agent Development / Request a Role.",
          "We scope MCP tools and templates so phone and chat stay on the same architecture.",
        ],
        relatedLinks: [
          { label: "Custom Agent Development", href: "/custom-agent-development" },
        ],
      },
    ],
  },
  {
    id: "human-in-the-loop",
    title: "Human in the Loop",
    description:
      "Warm transfers, Connect with Human CTAs, and inbox summaries so your team stays in control.",
    icon: "handoff",
    articles: [
      {
        id: "what-hitl-means",
        title: "What “human in the loop” means",
        summary: "AI covers volume; humans handle judgment and relationships.",
        body: [
          "Inbound GPT agents greet, qualify, answer from your knowledge, and book when tools are connected.",
          "When someone asks for a person — or the request is outside policy — the agent hands off with full context instead of looping forever.",
          "Your team remains accountable for exceptions, VIP relationships, and anything clinical or regulated. Inbound GPT does not provide medical advice.",
        ],
        relatedLinks: [
          { label: "AI Summary", href: "/ai-summary" },
          { label: "Docs — Team handoffs", href: "/documentation#handoffs" },
        ],
      },
      {
        id: "connect-with-human",
        title: "Connect with Human in chat",
        summary: "Keep a clear CTA in the widget.",
        body: [
          "Use Connect with Human (or your custom CTA) so visitors can escalate without friction.",
          "Staff should receive inbox notifications and conversation summaries so the visitor does not repeat themselves.",
        ],
        relatedLinks: [{ label: "Product — Responsibilities", href: "/product/responsibilities" }],
      },
    ],
  },
  {
    id: "agent-templates",
    title: "Agent Templates",
    description:
      "Clone Official and Moonlight agents, try Live Demo, and hire with unit-based pricing.",
    icon: "agents",
    articles: [
      {
        id: "browse-templates",
        title: "Browse and filter templates",
        summary: "Filter by industry, role, and responsibilities.",
        body: [
          "Open Agent Templates (/agents) and filter by category, job title, or channels.",
          "Each card shows Live Demo and Clone. Live Demo opens that agent’s profile widget; Clone starts hire onboarding.",
        ],
        relatedLinks: [
          { label: "Agent Templates", href: "/agents" },
          { label: "Customer Stories", href: "/customer-stories" },
        ],
      },
      {
        id: "live-demo-profile",
        title: "Live Demo and View Profile",
        summary: "Try channels before you hire.",
        body: [
          "On an agent profile, Live Demo channel buttons open the right-hand sidebar for that agent.",
          "Customer Stories cards link View Profile to the matched marketplace agent when available, and Live Demo switches the sidebar to that agent’s widget.",
        ],
        relatedLinks: [{ label: "Customer Stories", href: "/customer-stories" }],
      },
    ],
  },
];

export function getHelpTopic(id: string): HelpTopic | undefined {
  return HELP_TOPICS.find((t) => t.id === id);
}

export function getHelpArticle(
  topicId: string,
  articleId: string,
): { topic: HelpTopic; article: HelpArticle } | undefined {
  const topic = getHelpTopic(topicId);
  if (!topic) return undefined;
  const article = topic.articles.find((a) => a.id === articleId);
  if (!article) return undefined;
  return { topic, article };
}

export const HELP_TOPIC_IDS = HELP_TOPICS.map((t) => t.id);
